import {Organization} from '../types';
import {
    Anchor,
    Card,
    Divider,
    Group,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from '@mantine/core';
import {AiOutlineContacts} from 'react-icons/ai';
import {BiTime} from 'react-icons/bi';
import {Link} from 'react-router-dom';
import {RecordLogo} from '../images/RecordLogo';

const InfoRow = ({label, children}: { label: string; children: React.ReactNode }) => (
    <Group justify="space-between" wrap="nowrap">
        <Text size="sm" c="dimmed" style={{minWidth: 100}}>
            {label}
        </Text>
        <Group gap="xs" wrap="nowrap">
            {children}
        </Group>
    </Group>
);

const formatDate = (iso?: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

export const Profile = ({record}: { record: Organization }) => {

    const hasContactInfo = record.email || record.phone || record.url;

    return (
        <Stack gap="lg">
            {/* Organization Header */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group wrap="nowrap" gap="lg">
                    <RecordLogo record={record}/>
                    <Stack gap={4}>
                        <Title order={4}>{record.name}</Title>
                        {record.description && (
                            <Text size="sm" c="dimmed">{record.description}</Text>
                        )}
                        {record.adminAccountId && (
                            <Text size="xs" c="dimmed">
                                Admin:{' '}
                                <Anchor component={Link} to={`/accounts/${record.adminAccountId}`} size="xs">
                                    {record.adminAccountId}
                                </Anchor>
                            </Text>
                        )}
                    </Stack>
                </Group>
            </Card>

            {/* Contact Information */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group gap="xs" mb="md">
                    <ThemeIcon variant="outline" size="lg" color="blue">
                        <AiOutlineContacts size={20}/>
                    </ThemeIcon>
                    <Title order={5}>Contact</Title>
                </Group>
                {hasContactInfo ? (
                    <Stack gap="sm">
                        <InfoRow label="Email">
                            <Text size="sm">{record.email || '-'}</Text>
                        </InfoRow>
                        <Divider/>
                        <InfoRow label="Phone">
                            <Text size="sm">{record.phone || '-'}</Text>
                        </InfoRow>
                        <Divider/>
                        <InfoRow label="URL">
                            {record.url ? (
                                <Anchor href={record.url} target="_blank" size="sm">
                                    {record.url}
                                </Anchor>
                            ) : (
                                <Text size="sm">-</Text>
                            )}
                        </InfoRow>
                    </Stack>
                ) : (
                    <Text size="sm" c="dimmed" fs="italic">
                        No contact details added yet. Use the Edit button to add them.
                    </Text>
                )}
            </Card>

            {/* Metadata */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group gap="xs" mb="md">
                    <ThemeIcon variant="outline" size="lg" color="blue">
                        <BiTime size={20}/>
                    </ThemeIcon>
                    <Title order={5}>Details</Title>
                </Group>
                <Stack gap="sm">
                    <InfoRow label="Created By">
                        <Text size="sm">
                            {record.createdBy
                                ? [record.createdBy.givenName, record.createdBy.familyName].filter(Boolean).join(' ')
                                : record.createdByUsername || 'n/a'}
                        </Text>
                    </InfoRow>
                    <Divider/>
                    <SimpleGrid cols={2}>
                        <Group gap="xs">
                            <BiTime size={14} color="var(--mantine-color-dimmed)"/>
                            <Text size="xs" c="dimmed">
                                Created {formatDate(record.created)}
                            </Text>
                        </Group>
                        <Group gap="xs">
                            <BiTime size={14} color="var(--mantine-color-dimmed)"/>
                            <Text size="xs" c="dimmed">
                                Updated {formatDate(record.updated)}
                            </Text>
                        </Group>
                    </SimpleGrid>
                </Stack>
            </Card>
        </Stack>
    );
};
